import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useVehicleTemplates } from '../../../hooks';
import { PriceInput } from '../../../components/common';
import {
    VehicleType,
    VehicleCondition,
    CurrencyType,
    FuelType,
    TransmissionType
} from '../../../types/model/vehicle.model';
import styles from './CreateVehicleTemplate.module.css';

export const CreateVehicleTemplate: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const {
        addTemplate,
        editTemplate,
        loadTemplateById,
        selectedTemplate,
        isLoading
    } = useVehicleTemplates();

    const [formData, setFormData] = useState({
        name: '',
        brand: '',
        model: '',
        year: new Date().getFullYear(),
        price: 0,
        color: '',
        fuelType: FuelType.GASOLINE,
        transmission: TransmissionType.MANUAL,
        engine: '',
        doors: 4,
        seats: 5,
        description: '',
        currency: CurrencyType.USD,
        condition: VehicleCondition.NUEVO,
        vehicleType: VehicleType.AUTO,
        features: [] as string[],
        images: [] as string[],
    });

    // Cargar datos si estamos editando
    useEffect(() => {
        if (id) {
            loadTemplateById(id);
        }
    }, [id, loadTemplateById]);

    // Actualizar formulario cuando llega el template seleccionado
    useEffect(() => {
        if (id && selectedTemplate && selectedTemplate.id === id) {
            setFormData({
                name: selectedTemplate.name,
                brand: selectedTemplate.brand,
                model: selectedTemplate.model,
                year: selectedTemplate.year,
                price: selectedTemplate.price,
                color: selectedTemplate.color,
                fuelType: selectedTemplate.fuelType,
                transmission: selectedTemplate.transmission,
                engine: selectedTemplate.engine,
                doors: selectedTemplate.doors,
                seats: selectedTemplate.seats,
                description: selectedTemplate.description || '',
                currency: selectedTemplate.currency,
                condition: selectedTemplate.condition,
                vehicleType: selectedTemplate.vehicleType,
                features: selectedTemplate.features || [],
                images: selectedTemplate.images || [],
            });
        }
    }, [id, selectedTemplate]);

    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;

        // Para campos numéricos, convertir a número o mantener vacío
        let processedValue: string | number = value;
        if (name === 'year' || name === 'price' || name === 'doors' || name === 'seats') {
            processedValue = value === '' ? 0 : Number(value);
        }

        setFormData((prev) => ({
            ...prev,
            [name]: processedValue,
        }));

        // Limpiar error del campo al escribir
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleNumberInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Permitir: backspace, delete, tab, escape, enter, punto decimal (para precio)
        if (
            e.key === 'Backspace' ||
            e.key === 'Delete' ||
            e.key === 'Tab' ||
            e.key === 'Escape' ||
            e.key === 'Enter' ||
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight' ||
            e.key === 'Home' ||
            e.key === 'End' ||
            (e.key === '.' && e.currentTarget.name === 'price')
        ) {
            return;
        }

        // Bloquear si no es un número
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const scrollToError = (fieldName: string) => {
        const element = document.getElementById(fieldName);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            setTimeout(() => {
                element.focus();
            }, 300);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        let firstErrorField: string | null = null;

        if (!formData.name.trim()) {
            newErrors.name = 'El nombre del template es requerido';
            if (!firstErrorField) firstErrorField = 'name';
        }
        if (!formData.brand.trim()) {
            newErrors.brand = 'La marca es requerida';
            if (!firstErrorField) firstErrorField = 'brand';
        }
        if (!formData.model.trim()) {
            newErrors.model = 'El modelo es requerido';
            if (!firstErrorField) firstErrorField = 'model';
        }
        if (formData.year < 1900 || formData.year > new Date().getFullYear() + 1) {
            newErrors.year = 'Año inválido';
            if (!firstErrorField) firstErrorField = 'year';
        }
        if (formData.price <= 0) {
            newErrors.price = 'El precio debe ser mayor a 0';
            if (!firstErrorField) firstErrorField = 'price';
        }
        if (!formData.color.trim()) {
            newErrors.color = 'El color es requerido';
            if (!firstErrorField) firstErrorField = 'color';
        }
        if (!formData.engine.trim()) {
            newErrors.engine = 'El motor es requerido';
            if (!firstErrorField) firstErrorField = 'engine';
        }

        setErrors(newErrors);

        if (firstErrorField) {
            scrollToError(firstErrorField);
        }

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Por favor, completa todos los campos requeridos');
            return;
        }

        const loadingToast = toast.loading(id ? 'Actualizando template...' : 'Guardando template...');

        try {
            if (id) {
                await editTemplate(id, formData).unwrap();
                toast.success('¡Template actualizado exitosamente!', { icon: '✅' });
            } else {
                await addTemplate(formData).unwrap();
                toast.success('¡Template creado exitosamente!', { icon: '✅' });
            }

            toast.dismiss(loadingToast);

            setTimeout(() => {
                navigate('/vehicle-templates');
            }, 1000);
        } catch (error: any) {
            console.error('Error al guardar template:', error);
            toast.dismiss(loadingToast);
            toast.error(error?.message || 'Error al guardar el template. Por favor, intenta nuevamente.', {
                duration: 5000,
            });
        }
    };

    const handleCancel = () => {
        navigate('/vehicle-templates');
    };

    return (
        <div className={styles.addVehicleContainer}>
            <div className={styles.header}>
                <h1>{id ? 'Editar Template de Vehículo' : 'Crear Nuevo Template de Vehículo'}</h1>
                <p>{id ? 'Modifica los datos del modelo base' : 'Define un modelo base para agilizar la carga de vehículos'}</p>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
                {/* Información del Template */}
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>📝 Información del Template</h2>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label htmlFor="name" className={styles.required}>
                                Nombre del Template
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Ej: Toyota Corolla 2024 Base"
                                className={errors.name ? styles.inputError : ''}
                            />
                            {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                        </div>
                    </div>
                </div>

                {/* Información Básica */}
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>📋 Información Básica del Vehículo</h2>

                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label htmlFor="brand" className={styles.required}>
                                Marca
                            </label>
                            <input
                                type="text"
                                id="brand"
                                name="brand"
                                value={formData.brand}
                                onChange={handleChange}
                                placeholder="Ej: Toyota"
                                className={errors.brand ? styles.inputError : ''}
                            />
                            {errors.brand && <span className={styles.errorText}>{errors.brand}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="vehicleType" className={styles.required}>
                                Tipo de Vehículo
                            </label>
                            <select
                                id="vehicleType"
                                name="vehicleType"
                                value={formData.vehicleType}
                                onChange={handleChange}
                            >
                                <option value={VehicleType.AUTO}>Auto</option>
                                <option value={VehicleType.MOTO}>Moto</option>
                                <option value={VehicleType.CAMIONETA}>Camioneta</option>
                                <option value={VehicleType.CAMION}>Camión</option>
                                <option value={VehicleType.CUATRICICLO}>Cuatriciclo</option>
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="model" className={styles.required}>
                                Modelo
                            </label>
                            <input
                                type="text"
                                id="model"
                                name="model"
                                value={formData.model}
                                onChange={handleChange}
                                placeholder="Ej: Corolla"
                                className={errors.model ? styles.inputError : ''}
                            />
                            {errors.model && <span className={styles.errorText}>{errors.model}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="year" className={styles.required}>
                                Año
                            </label>
                            <input
                                type="number"
                                id="year"
                                name="year"
                                value={formData.year}
                                onChange={handleChange}
                                onKeyDown={handleNumberInput}
                                min="1900"
                                max={new Date().getFullYear() + 1}
                                className={errors.year ? styles.inputError : ''}
                            />
                            {errors.year && <span className={styles.errorText}>{errors.year}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="condition" className={styles.required}>
                                Condición
                            </label>
                            <select
                                id="condition"
                                name="condition"
                                value={formData.condition}
                                onChange={handleChange}
                            >
                                <option value={VehicleCondition.NUEVO}>Nuevo</option>
                                <option value={VehicleCondition.USADO}>Usado</option>
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="color" className={styles.required}>
                                Color
                            </label>
                            <input
                                type="text"
                                id="color"
                                name="color"
                                value={formData.color}
                                onChange={handleChange}
                                placeholder="Ej: Blanco"
                                className={errors.color ? styles.inputError : ''}
                            />
                            {errors.color && <span className={styles.errorText}>{errors.color}</span>}
                        </div>
                    </div>
                </div>

                {/* Especificaciones Técnicas */}
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>🔧 Especificaciones Técnicas</h2>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label htmlFor="engine" className={styles.required}>
                                Motor
                            </label>
                            <input
                                type="text"
                                id="engine"
                                name="engine"
                                value={formData.engine}
                                onChange={handleChange}
                                placeholder="Ej: 1.8L Hybrid"
                                className={errors.engine ? styles.inputError : ''}
                            />
                            {errors.engine && <span className={styles.errorText}>{errors.engine}</span>}
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="fuelType" className={styles.required}>
                                Combustible
                            </label>
                            <select
                                id="fuelType"
                                name="fuelType"
                                value={formData.fuelType}
                                onChange={handleChange}
                            >
                                <option value={FuelType.GASOLINE}>Nafta</option>
                                <option value={FuelType.DIESEL}>Diesel</option>
                                <option value={FuelType.ELECTRIC}>Eléctrico</option>
                                <option value={FuelType.HYBRID}>Híbrido</option>
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="transmission" className={styles.required}>
                                Transmisión
                            </label>
                            <select
                                id="transmission"
                                name="transmission"
                                value={formData.transmission}
                                onChange={handleChange}
                            >
                                <option value={TransmissionType.MANUAL}>Manual</option>
                                <option value={TransmissionType.AUTOMATIC}>Automática</option>
                                <option value={TransmissionType.SEMI_AUTOMATIC}>Semi-Automática</option>
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="doors" className={styles.required}>
                                Puertas
                            </label>
                            <input
                                type="number"
                                id="doors"
                                name="doors"
                                value={formData.doors}
                                onChange={handleChange}
                                onKeyDown={handleNumberInput}
                                min="2"
                                max="5"
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="seats" className={styles.required}>
                                Asientos
                            </label>
                            <input
                                type="number"
                                id="seats"
                                name="seats"
                                value={formData.seats}
                                onChange={handleChange}
                                onKeyDown={handleNumberInput}
                                min="1"
                                max="50"
                            />
                        </div>
                    </div>
                </div>

                {/* Información Comercial */}
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>💰 Información Comercial</h2>

                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label htmlFor="price" className={styles.required}>
                                Precio Sugerido
                            </label>
                            <PriceInput
                                value={formData.price}
                                currency={formData.currency}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, price: val }));
                                    if (errors.price) {
                                        setErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors.price;
                                            return newErrors;
                                        });
                                    }
                                }}
                                onCurrencyChange={(curr) => setFormData(prev => ({ ...prev, currency: curr }))}
                                error={errors.price}
                            />
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="description">
                            Descripción / Observaciones
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Descripción detallada del vehículo..."
                            rows={4}
                        />
                    </div>
                </div>

                {/* Botones de Acción */}
                <div className={styles.formActions}>
                    <button
                        type="button"
                        onClick={handleCancel}
                        className={styles.cancelButton}
                        disabled={isLoading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Guardando...' : (id ? '✓ Actualizar Template' : '✓ Crear Template')}
                    </button>
                </div>
            </form>
        </div>
    );
};
